import React, { useState } from 'react';
import { Card, Spin, Empty } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';

import { MaterialReservation } from '../../../../../interfaces/MaterialInventory.interface';
import { UserListInterface } from '../../../../../interfaces/User.interface.ts';
import CutActions from '../CutActions.tsx';
import { useNavigate } from 'react-router';
import { useLocationLabel } from '../../../../../utils/locationLabel';

interface IMaterialReservations {
	count: number;
	results: MaterialReservation[];
}

interface IProps {
	userData: UserListInterface[];
	materialReservations: IMaterialReservations;
	loadingMaterialReservations: boolean;
	setMaterialReservations: (value: IMaterialReservations) => void;
	handleClickCutItem: (
		action: string,
		material_reservation: MaterialReservation
	) => void;
	pagination?: TablePaginationConfig | false;
}

const statusBorderColor: Record<string, string> = {
	Cut: '#52c41a',
	'In Progress': '#1677ff',
	'Not Cut': '#d9d9d9',
};

const ExpandableContent: React.FC<IProps> = ({
	userData,
	materialReservations,
	loadingMaterialReservations,
	handleClickCutItem,
}) => {
	const [radioButtonLoading] = useState<boolean>(false);
	const navigate = useNavigate();
	const formatLocation = useLocationLabel();

	if (loadingMaterialReservations) {
		return (
			<div style={{ textAlign: 'center', padding: 32 }}>
				<Spin size="large" />
			</div>
		);
	}

	if (!materialReservations?.results?.length) {
		return <Empty description="No reservations" />;
	}

	return (
		<div
			style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 360px))',
				justifyContent: 'start',
				gap: 16,
			}}
		>
			{materialReservations.results.map((record) => {
				const user = record.cut_by
					? userData?.find((u) => String(u.id) === String(record.cut_by))
					: null;
				const barId =
					(record?.material_length?.bundle || '') +
					(record?.material_length?.bundle && record?.material_length?.friendly_id
						? ' | '
						: '') +
					(record?.material_length?.friendly_id || '');
				const borderColor =
					statusBorderColor[record.material_cut_status] ?? '#d9d9d9';

				return (
					<Card
						key={record.id}
						style={{
							borderLeft: `4px solid ${borderColor}`,
							fontSize: 16,
							width: '100%',
							minWidth: 0,
						}}
						styles={{ body: { padding: 16 } }}
					>
						<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
							{/* Bar ID */}
							<div>
								<span style={{ color: '#888', fontSize: 16 }}>Bar ID</span>
								<div style={{ fontSize: 16, fontWeight: 600 }}>
									<a
										style={{ fontSize: 16 }}
										onClick={() =>
											navigate(
												`/material/materials?search=${record?.material_length?.material?.id}`
											)
										}
									>
										{barId || '—'}
									</a>
								</div>
							</div>

							{/* Lengths + Cut Progress */}
							<div>
								<div
									style={{
										display: 'grid',
										gridTemplateColumns: '1fr 1fr 1fr',
										gap: 8,
										fontSize: 16,
									}}
								>
									<div>
										<div style={{ color: '#888', fontSize: 16 }}>Purch. Len</div>
										<div style={{ fontWeight: 500 }}>
											{record?.material_length?.purchased_length ?? '—'}
										</div>
									</div>
									<div>
										<div style={{ color: '#888', fontSize: 16 }}>Curr. Len</div>
										<div style={{ fontWeight: 500 }}>
											{record?.material_length?.current_length ?? '—'}
										</div>
									</div>
									<div>
										<div style={{ color: '#888', fontSize: 16 }}>Cut</div>
										<div style={{ fontWeight: 500 }}>
											{record?.actual_cut_quantity} / {record.material_cut_quantity}
										</div>
									</div>
								</div>
							</div>

						{/* Location + Cut By */}
						<div>
							<div
								style={{
									display: 'grid',
									gridTemplateColumns: '1fr 1fr',
									gap: 8,
									fontSize: 16,
								}}
							>
								<div>
									<div style={{ color: '#888', fontSize: 16 }}>Location</div>
									<div style={{ fontWeight: 500, wordBreak: 'break-word' }}>
										{formatLocation(record?.material_length?.inventory_location?.display_name) ?? '—'}
									</div>
								</div>
								<div>
									<div style={{ color: '#888', fontSize: 16 }}>Cut By</div>
									<div style={{ fontWeight: 500 }}>
										{user?.full_name || user?.first_name || '—'}
									</div>
								</div>
							</div>
						</div>

						{/* Saw Kerf + Trim Cut */}
						<div>
							<div
								style={{
									display: 'grid',
									gridTemplateColumns: '1fr 1fr',
									gap: 8,
									fontSize: 16,
								}}
							>
								<div>
									<div style={{ color: '#888', fontSize: 16 }}>Saw Kerf</div>
									<div style={{ fontWeight: 500 }}>
										{record?.saw_kerf ?? '—'}"
									</div>
								</div>
								<div>
									<div style={{ color: '#888', fontSize: 16 }}>Trim Cut</div>
									<div style={{ fontWeight: 500 }}>
										{record?.required_trim_cut_length ?? '—'}"
									</div>
								</div>
							</div>
						</div>

							{/* Status actions */}
							<div
								style={{
									display: 'flex',
									justifyContent: 'center',
									paddingInline: 8,
								}}
								onClick={(e) => e.stopPropagation()}
							>
								<Spin spinning={radioButtonLoading}>
									<CutActions
										styles={{
											flexDirection: 'row',
											flexWrap: 'wrap',
											justifyContent: 'center',
											gap: 8,
											width: '100%',
										}}
										cut_status={record.material_cut_status}
										disableNotCut={(record.actual_cut_quantity ?? 0) > 0}
										handleClick={(value) => handleClickCutItem(value, record)}
									/>
								</Spin>
							</div>
						</div>
					</Card>
				);
			})}
		</div>
	);
};

export default ExpandableContent;
